import React, { useEffect, useMemo, useState } from 'react';
import type { CommercialDashboardQuery, DashboardPeriodMode, GoalPeriod } from '@troia-v3/types';

import { Icon } from '@/shared/icons';
import { PageToolbar, Breadcrumb, PeriodPicker, Select } from '@/shared/components/ui';
import type { PeriodPickerValue } from '@/shared/components/ui';
import { useUnitsDropdown } from '@/shared/hooks/useUnitsDropdown';
import { ALL_UNITS, buildUnitFilterSelect, unitFilterToQuery } from '@/shared/utils/unitFilter';
import { useCommercialDashboard } from '../hooks/useCommercialDashboard';
import { useCommercialDashboardPreferences } from '../hooks/useCommercialDashboardPreferences';
import { DEFAULT_ACTIVITY_WIDGETS, DEFAULT_SUMMARY_WIDGETS } from '../config/widgets';
import { WidgetsSection } from '../components/WidgetsSection';
import { MetaGaugeCard } from '../components/MetaGaugeCard';
import { RevenueChartCard } from '../components/RevenueChartCard';
import { ComparisonCard } from '../components/ComparisonCard';
import { TopPerformersSpotlight } from '../components/TopPerformersSpotlight';
import { ScorecardTable } from '../components/ScorecardTable';
import { WeeklyHeatmapCard } from '../components/WeeklyHeatmapCard';
import { TopCampaignsCard } from '../components/TopCampaignsCard';

/**
 * Dashboard de Gestão Comercial — Design System (PageToolbar + grid de cards).
 *
 * Consome `GET /api/dashboards/commercial` agregando KPIs, meta, receita,
 * comparações, atividades, top performers, scorecard, heatmap e campanhas.
 * Período via shared PeriodPicker (F81); unidade/estado pelo mesmo seletor do
 * Analytics. Blocos de widgets, colunas do time, modo do gráfico/comparativo
 * e unidade ficam salvos no navegador (`useCommercialDashboardPreferences`).
 */
export const CommercialDashboardPage: React.FC = () => {
  const now = useMemo(() => new Date(), []);
  const [period, setPeriod] = useState<DashboardPeriodMode>('month');
  const [customRange, setCustomRange] = useState<{ startDate: string; endDate: string } | null>(
    null,
  );
  const [year] = useState<number>(now.getUTCFullYear());
  const [month] = useState<number>(now.getUTCMonth() + 1);
  const prefs = useCommercialDashboardPreferences();
  const { unitFilter, setUnitFilter } = prefs;

  // TODAS as unidades ativas (não as do payload), para dar para trocar de
  // unidade mesmo com uma escolhida — mesmo motivo do Analytics.
  const { data: units } = useUnitsDropdown();
  const unitSelect = useMemo(() => buildUnitFilterSelect(units ?? []), [units]);

  // A unidade salva pode não existir mais (excluída, ou de outra empresa na
  // troca de conta): com a lista carregada, valor desconhecido volta a "Todas".
  useEffect(() => {
    if (!units || unitFilter === ALL_UNITS) return;
    const known = [
      ...unitSelect.options,
      ...(unitSelect.groups ?? []).flatMap((g) => g.options),
    ].some((o) => o.value === unitFilter);
    if (!known) setUnitFilter(ALL_UNITS);
  }, [units, unitSelect, unitFilter, setUnitFilter]);

  const queryParams = useMemo<CommercialDashboardQuery>(() => {
    const unit = unitFilterToQuery(unitFilter);
    if (period === 'custom' && customRange) {
      return { period: 'custom', startDate: customRange.startDate, endDate: customRange.endDate, ...unit };
    }
    if (period === 'month') return { period: 'month', year, month, ...unit };
    if (period === 'year') return { period: 'year', year, ...unit };
    return { period: 'week', ...unit };
  }, [period, customRange, year, month, unitFilter]);

  const { data, isLoading, error, refetch, isFetching } = useCommercialDashboard(queryParams);

  const goalInitial = useMemo<{
    period: GoalPeriod;
    year: number;
    monthOrQuarter: number;
  }>(() => {
    if (period === 'year') {
      return { period: 'year', year, monthOrQuarter: 1 };
    }
    if (period === 'custom' && customRange) {
      const start = new Date(`${customRange.startDate}T00:00:00Z`);
      return {
        period: 'month',
        year: start.getUTCFullYear(),
        monthOrQuarter: start.getUTCMonth() + 1,
      };
    }
    if (period === 'month') {
      return { period: 'month', year, monthOrQuarter: month };
    }
    return {
      period: 'month',
      year: now.getUTCFullYear(),
      monthOrQuarter: now.getUTCMonth() + 1,
    };
  }, [period, customRange, year, month, now]);

  const handlePeriodChange = (value: PeriodPickerValue): void => {
    setPeriod(value.mode);
    if (value.mode === 'custom' && value.start && value.end) {
      setCustomRange({ startDate: value.start, endDate: value.end });
    }
  };

  const pickerValue: PeriodPickerValue =
    period === 'custom' && customRange
      ? { mode: 'custom', start: customRange.startDate, end: customRange.endDate }
      : { mode: period };

  const headerLabel = data?.period?.label ?? '';

  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="commercial-dashboard-page"
    >
      {/* Toolbar única do DS (F23 — PageToolbar 56px, token header) */}
      <div className="flex-none sticky top-0 z-20">
        <PageToolbar
          data-testid="commercial-dashboard-toolbar"
          left={
            <>
              <Breadcrumb
                items={[
                  { label: 'Dashboards' },
                  { label: 'Gestão Comercial', testid: 'commercial-breadcrumb-current' },
                ]}
                mobileLegend={`Dashboards${headerLabel ? ` · ${headerLabel}` : ''}`}
                data-testid="commercial-breadcrumb"
              />
              {headerLabel && (
                <span
                  className="hidden sm:inline-flex items-center h-[26px] px-2.5 rounded-full text-xs font-semibold flex-shrink-0"
                  style={{
                    backgroundColor: 'var(--theme-surface-sunken)',
                    color: 'var(--theme-muted-text)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  data-testid="dashboard-period-label"
                >
                  {headerLabel}
                </span>
              )}
            </>
          }
          right={
            <div className="flex items-center gap-2 flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-visible">
              {/* Unidade ou estado — filtra leads, vendas, visitas e atividades */}
              {(units?.length ?? 0) > 0 && (
                <div className="flex-shrink-0 w-[200px]">
                  <Select
                    value={unitFilter}
                    onChange={setUnitFilter}
                    options={unitSelect.options}
                    {...(unitSelect.groups ? { groups: unitSelect.groups } : {})}
                    aria-label="Unidade ou estado"
                    style={{ height: 36 }}
                    data-testid="dashboard-unit"
                  />
                </div>
              )}
              {/* Período — controle direto, shared PeriodPicker (F81) */}
              <PeriodPicker
                value={pickerValue}
                onChange={handlePeriodChange}
                data-testid="dashboard-period"
              />
              {/* Atualizar — 36px bordado, ícone 16, com hover (F07) */}
              <button
                type="button"
                onClick={() => void refetch()}
                className="w-9 h-9 rounded-lg border flex items-center justify-center flex-shrink-0 transition-colors"
                style={{
                  borderColor: 'var(--theme-input-border)',
                  backgroundColor: 'var(--theme-card-background)',
                  color: 'var(--theme-muted-text)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--theme-card-background)';
                }}
                title="Atualizar"
                aria-label="Atualizar"
                data-testid="dashboard-refresh"
              >
                <Icon name="refresh" size={16} className={isFetching ? 'animate-spin' : ''} />
              </button>
            </div>
          }
        />
      </div>

      {/* Corpo scrollável */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 space-y-5">
          {error && (
            <div
              className="rounded-xl p-4"
              style={{
                backgroundColor: 'var(--theme-error-subtle)',
                border: '1px solid var(--theme-card-border)',
                color: 'var(--theme-error-fg)',
              }}
              data-testid="dashboard-error"
            >
              Erro ao carregar dashboard: {(error as Error).message}
            </div>
          )}

          {isLoading && (
            <div
              className="rounded-xl p-8 text-center"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                border: '1px solid var(--theme-card-border)',
              }}
              data-testid="dashboard-loading"
            >
              <p style={{ color: 'var(--theme-muted-text)' }}>Carregando…</p>
            </div>
          )}

          {data && !isLoading && (
            <div className="flex flex-col gap-5">
              {/* Resumo do período — widgets personalizáveis */}
              <WidgetsSection
                title="Resumo do período"
                data={data}
                selected={prefs.summaryWidgets}
                onChange={prefs.setSummaryWidgets}
                defaults={DEFAULT_SUMMARY_WIDGETS}
                testId="dashboard-summary-widgets"
              />

              {/* Three Pillars (Meta | Receita | Comparativo) */}
              <div className="grid grid-cols-1 min-[1080px]:grid-cols-4 gap-4 items-stretch">
                <div className="min-[1080px]:col-span-1 h-full min-h-[260px]">
                  <MetaGaugeCard
                    goal={data.goal}
                    initialPeriod={goalInitial.period}
                    initialYear={goalInitial.year}
                    initialMonthOrQuarter={goalInitial.monthOrQuarter}
                    unitFiltered={unitFilter !== ALL_UNITS}
                  />
                </div>
                <div className="min-[1080px]:col-span-2 h-full min-h-[260px]">
                  <RevenueChartCard
                    data={data.revenueChart}
                    mode={prefs.chartMode}
                    onModeChange={prefs.setChartMode}
                  />
                </div>
                <div className="min-[1080px]:col-span-1 h-full min-h-[260px]">
                  <ComparisonCard
                    comparison={data.comparison}
                    mode={prefs.comparisonMode}
                    onModeChange={prefs.setComparisonMode}
                  />
                </div>
              </div>

              {/* Atividades do time — widgets personalizáveis */}
              <WidgetsSection
                title="Atividades do time"
                data={data}
                selected={prefs.activityWidgets}
                onChange={prefs.setActivityWidgets}
                defaults={DEFAULT_ACTIVITY_WIDGETS}
                testId="dashboard-activity-widgets"
              />

              {/* Top 5 spotlight */}
              <TopPerformersSpotlight performers={data.topPerformers} />

              {/* Scorecard */}
              <ScorecardTable
                rows={data.scorecard}
                columns={prefs.scorecardColumns}
                onColumnsChange={prefs.setScorecardColumns}
              />

              {/* Heatmap | Top campanhas */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <WeeklyHeatmapCard weeks={data.weeklyHeatmap} />
                <TopCampaignsCard campaigns={data.topCampaigns} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CommercialDashboardPage.displayName = 'CommercialDashboardPage';
